"use client";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";

/**
 * Client-side pricing overlay.
 *
 * Inventory HTML/RSC payloads are price-free for every visitor. Employees get
 * prices after hydration: cards and the item page register their ids here,
 * requests are batched into one call to the session-gated /api/items/pricing
 * endpoint, and the resulting map is pushed to consumers. Anonymous visitors
 * never trigger a request.
 *
 * Two contexts on purpose (same pattern as InventoryList.ResultsContext): the
 * request function has a stable identity so registering an id never re-renders
 * anything; only components that read the price map re-render when prices
 * arrive.
 */
type PriceMap = Record<number, number | null>;

const PriceMapContext = createContext<PriceMap>({});
const RequestPricesContext = createContext<(ids: number[]) => void>(() => {});

const BATCH_DELAY_MS = 50; // collapse a page of card mounts into one request
const CHUNK_SIZE = 100;    // matches the endpoint's MAX_IDS

export function PricingProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const isEmployee = !!session?.user;

  const [priceMap, setPriceMap] = useState<PriceMap>({});
  // Queued ids not yet fetched; ids already sent (avoid duplicate fetches).
  const pendingRef = useRef<Set<number>>(new Set());
  const sentRef = useRef<Set<number>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isEmployeeRef = useRef(isEmployee);

  const flush = useCallback(async () => {
    timerRef.current = null;
    if (!isEmployeeRef.current) return; // keep queued until session resolves
    const ids = [...pendingRef.current].filter((id) => !sentRef.current.has(id));
    pendingRef.current.clear();
    if (ids.length === 0) return;
    ids.forEach((id) => sentRef.current.add(id));

    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      const chunk = ids.slice(i, i + CHUNK_SIZE);
      try {
        const res = await fetch(`/api/items/pricing?ids=${chunk.join(',')}`);
        if (!res.ok) {
          // Allow a later retry (e.g. transient error or session expiry).
          chunk.forEach((id) => sentRef.current.delete(id));
          continue;
        }
        const data = await res.json();
        setPriceMap((prev) => ({ ...prev, ...(data?.prices ?? {}) }));
      } catch {
        chunk.forEach((id) => sentRef.current.delete(id));
      }
    }
  }, []);

  const requestPrices = useCallback((ids: number[]) => {
    let queued = false;
    for (const id of ids) {
      if (!sentRef.current.has(id)) {
        pendingRef.current.add(id);
        queued = true;
      }
    }
    if (!queued || timerRef.current) return;
    timerRef.current = setTimeout(flush, BATCH_DELAY_MS);
  }, [flush]);

  // Session resolves after cards have already registered their ids — flush the
  // queue the moment we know the viewer is an employee.
  useEffect(() => {
    isEmployeeRef.current = isEmployee;
    if (isEmployee && pendingRef.current.size > 0 && !timerRef.current) {
      timerRef.current = setTimeout(flush, 0);
    }
  }, [isEmployee, flush]);

  return (
    <RequestPricesContext.Provider value={requestPrices}>
      <PriceMapContext.Provider value={priceMap}>
        {children}
      </PriceMapContext.Provider>
    </RequestPricesContext.Provider>
  );
}

/** Read the fetched prices (re-renders when new prices arrive). */
export function usePriceMap() {
  return useContext(PriceMapContext);
}

/** Stable registration function — safe to call from effects without deps churn. */
export function useRequestPrices() {
  return useContext(RequestPricesContext);
}
